import { Add } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { listPageTableRowStyling } from '../../../styles/sxStyling';
import { Event } from '../../../types/Event';
import { Task, TaskStatus } from '../../../types/Task';
import { TaskTemplateLink } from '../../../types/TaskTemplate';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import { addOrRemoveDaysToDate, humanizeDate } from '../../../utility/dateUtils';
import { renderTaskStartCondition } from '../../../utility/task';
import AccordionSection from '../../AccordionSection';
import DrawerLink from '../../common/drawers/ModuleDrawer/DrawerLink';
import EventAddTaskModal from './EventAddTaskModal';
import TaskStatusPill from '../../tasks/TaskStatusPill';

interface TasksSectionProps {
  event: Event;
  setTasks: (tasks: Task[]) => void;
}

const TasksSection: React.FC<TasksSectionProps> = ({ event, setTasks }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedTaskTemplateLink, setSelectedTaskTemplateLink] = useState<TaskTemplateLink | null>(null);
  const { disabled } = useContext(DisableContext);
  const tasks = _.sortBy(event.tasks || [], 'due_date');

  // Add or update task
  const handleAddOrUpdateTask = useCallback(
    (template: TaskTemplateLink) => {
      if (!template) return;

      const existingTask = _.find(tasks, { uuid: template.uuid });

      const eventDt = DateTime.fromISO(event.start_at);
      const computedDate = eventDt.isValid ? eventDt.toISODate() : DateTime.now().toISODate();
      const updatedTask: Task = {
        uuid: template.uuid || crypto.randomUUID(),
        task_template_uuid: template.task_template_uuid || '',
        name: template.task_template_name || '',
        start_date: template.start_date,
        due_date: addOrRemoveDaysToDate({
          date: computedDate,
          days: template.due_date_offset || 0,
        }),
        start_date_offset: template.start_date_offset,
        due_date_offset: template.due_date_offset || 0,
        type_uuid: template.task_type_uuid,
        type_value: template.task_type_value,
        employee_display_name: template.assigned_to_display_name || '',
        employee_uuid: template.assigned_to_uuid || '',
        status: existingTask?.status || TaskStatus.NOT_STARTED,
        fields: existingTask?.fields || [],
        steps: existingTask?.steps || [],
        tags: existingTask?.tags || [],
        discarded_at: null,
        depends_on_uuid: template.depends_on_uuid,
        new_record: existingTask?.new_record || true,
      };

      const updatedTasks = existingTask ? _.map(tasks, (task) => (task.uuid === updatedTask.uuid ? updatedTask : task)) : [...tasks, updatedTask];

      setTasks(updatedTasks);
      setModalOpen(false);
      setSelectedTaskTemplateLink(null);
    },
    [tasks, setTasks, event.start_at]
  );

  const handleEditTask = (task: Task) => {
    setSelectedTaskTemplateLink({
      event_uuid: event.uuid,
      account_uuid: event.account_uuid,
      task_template_uuid: task.task_template_uuid,
      task_template_name: task.name,
      start_date_offset: task.start_date_offset,
      start_date: task.start_date,
      depends_on_uuid: task.depends_on_uuid,
      due_date_offset: task.due_date_offset || 0,
      assigned_to_uuid: task.employee_uuid,
      assigned_to_display_name: task.employee_display_name,
      task_type_uuid: task.type_uuid,
      task_type_value: task.type_value,
      uuid: task.uuid,
    });
    setModalOpen(true);
  };

  const handleRemoveTask = (taskUuid: string) => {
    const updatedTasks = _.reject(tasks, { uuid: taskUuid });
    setTasks(updatedTasks);
  };

  const taskTableRows = tasks.map((task) => (
    <TableRow key={task.uuid || crypto.randomUUID()} sx={listPageTableRowStyling}>
      <TableCell>
        {task.new_record ? (
          <Typography>{task.name}</Typography>
        ) : (
          <DrawerLink module={UserConfigurableModule.TASKS} uuid={task.uuid}>
            {task.name}
          </DrawerLink>
        )}
      </TableCell>
      <TableCell>
        <Typography>
          {renderTaskStartCondition(
            task,
            event.tasks.map((t) => ({ uuid: t.uuid, name: t.name })),
            event.start_at
          )}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography>{humanizeDate(task.due_date)}</Typography>
      </TableCell>
      <TableCell>
        {task.employee_uuid ? (
          <DrawerLink module={UserConfigurableModule.EMPLOYEES} uuid={task.employee_uuid}>
            {task.employee_display_name}
          </DrawerLink>
        ) : (
          <Typography>{task.employee_display_name}</Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography>{task.type_value}</Typography>
      </TableCell>
      <TableCell>
        <TaskStatusPill status={task.status} />
      </TableCell>
      <TableCell>
        {!disabled && (
          <>
            <IconButton onClick={() => handleEditTask(task)}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => handleRemoveTask(task.uuid)}>
              <DeleteIcon />
            </IconButton>
          </>
        )}
      </TableCell>
    </TableRow>
  ));
  return (
    <>
      <AccordionSection
        title="Tasks"
        headerElements={
          <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => setModalOpen(true)} disabled={disabled}>
            Add
          </ResponsiveButton>
        }
        collapseId={UserSettingsCollapseKeys.EVENT_ITEM_TASKS}
        hasData={tasks.length > 0}
        scrollable
      >
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Start Condition</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Task Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>{taskTableRows}</TableBody>
          </Table>
        </TableContainer>
      </AccordionSection>
      <EventAddTaskModal
        isOpen={isModalOpen}
        onCancel={() => {
          setSelectedTaskTemplateLink(null);
          setModalOpen(false);
        }}
        onAdd={handleAddOrUpdateTask}
        taskTemplateLink={selectedTaskTemplateLink}
        dependencyOptions={tasks}
      />
    </>
  );
};

export default TasksSection;
